/*
 * Scentt bag extras - free samples, "Make it a gift?" and gift message (snippets/scentt-cart-extras.liquid).
 * <scentt-cart-extras data-sample-label data-fix="key=qty,key=qty">
 *   button[data-scentt-sample="<variant id>" data-key?]  add / remove a free sample (_sample line property)
 *   [data-scentt-samples-list] + [data-scentt-samples-nav] the samples row scrolls sideways; the arrows show only when
 *                                                         the samples don't all fit
 *   input[data-scentt-gift-toggle data-scentt-gift-variant? data-key?]
 *                                                         "Make it a gift?": saves "Gift: Yes", adds the gift product
 *                                                         (one, if set) and shows the message box; unticked: removes
 *                                                         the gift product and clears "Gift" + "Gift message"
 *   [data-scentt-gift-stale]                              gift product removed elsewhere: leftover details are cleared
 *   [data-scentt-gift-sync]                               gift product in the bag without "Gift: Yes": it is saved
 *   [data-scentt-gift-line]                               the gift product is in the bag
 *   textarea[data-scentt-giftmsg]                         saved (debounced) as cart attribute "Gift message"
 * Every gift save runs one after another (queue), so a slower request can never overwrite a newer choice.
 * Changes inside this element never reach Trade's <cart-items>, whose change handler treats every change as a
 * quantity edit (it would mark the tick box / message invalid and block checkout on the cart page).
 * data-fix: bag limits worked out by the snippet - one gift product, one of each sample, no more samples than allowed,
 *   no samples once the bag stops qualifying. The lines are set to those quantities in one request.
 * The drawer and the cart page re-render the element after every change; connectedCallback runs again.
 */
(() => {
  const start = () => {
    const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
    if (!S || customElements.get('scentt-cart-extras')) return;

    const KEY = /^[\w:]{1,120}$/;
    const fixing = new Set(); /* line keys already being corrected (survives re-renders) */
    const once = { stale: false, sync: false, focusGift: false }; /* survives the bag re-rendering itself */
    let giftQueue = Promise.resolve();
    /** Runs gift saves strictly in order; the returned promise settles with this job's own result. */
    const queued = (job) => {
      const run = giftQueue.then(job, job);
      giftQueue = run.catch(() => {});
      return run;
    };
    const drawerOpen = () => {
      const d = document.querySelector('cart-drawer');
      return Boolean(d && d.classList.contains('active'));
    };

    class ScenttCartExtras extends HTMLElement {
      connectedCallback() {
        this.error = this.querySelector('[data-scentt-cart-error]');
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onInput = this.onInput.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        this.addEventListener('input', this.onInput);
        this.setupSamplesRow();
        this.fixLimits();
        this.clearStaleGift();
        this.syncGift();
        if (once.focusGift) {
          once.focusGift = false;
          const area = this.querySelector('[data-scentt-gift-box]:not([hidden]) [data-scentt-giftmsg]');
          if (area) area.focus({ preventScroll: true });
        }
      }

      /** The gift product was removed from the bag (bin icon): drop the leftover "Gift" details from the order. */
      clearStaleGift() {
        if (once.stale || !this.querySelector('[data-scentt-gift-stale]')) return;
        once.stale = true;
        queued(() => this.saveAttributes(false, '')).catch((e) => S.warn('gift cleanup', e)).finally(() => { once.stale = false; });
      }

      /** The gift product is in the bag (e.g. added from its product page) but "Gift: Yes" is missing: save it. */
      syncGift() {
        if (once.sync || !this.querySelector('[data-scentt-gift-sync]')) return;
        once.sync = true;
        const area = this.querySelector('[data-scentt-giftmsg]');
        queued(() => this.saveAttributes(true, area ? area.value : '')).catch((e) => S.warn('gift sync', e)).finally(() => { once.sync = false; });
      }

      saveAttributes(on, message) {
        // An empty value removes the attribute from the order
        return S.fetchJSON('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { Gift: on ? 'Yes' : '', 'Gift message': on ? String(message || '').slice(0, 250) : '' } }),
        });
      }

      disconnectedCallback() {
        if (this.rowObserver) this.rowObserver.disconnect();
        if (this.row && this.onRowScroll) this.row.removeEventListener('scroll', this.onRowScroll);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('input', this.onInput);
        if (this.pending) { clearTimeout(this.pending); this.saveMessage(); }
      }

      fail(err) {
        if (!this.error) return;
        this.error.textContent = (err && err.userMessage) || S.config.strings.error;
        this.error.hidden = false;
      }

      busy(el, on) {
        el.classList.toggle('loading', on);
        el.disabled = on;
        el.setAttribute('aria-busy', on ? 'true' : 'false');
      }

      /** Puts the bag back within its limits (see data-fix above). */
      async fixLimits() {
        const lines = Object.create(null);
        String(this.dataset.fix || '').split(',').slice(0, 20).forEach((part) => {
          const m = /^(\d{1,20}:\w{1,100})=([01])$/.exec(part.trim()); /* Shopify line key: variant id + ':' + hash */
          if (m && !fixing.has(m[1])) lines[m[1]] = Number(m[2]);
        });
        const keys = Object.keys(lines);
        if (!keys.length) return;
        keys.forEach((k) => fixing.add(k));
        try {
          await S.cart.set(lines, { open: drawerOpen(), source: 'scentt-bag-limits' });
        } catch (e) {
          keys.forEach((k) => fixing.delete(k)); /* try again on the next bag update */
          S.warn('bag limits', e);
        }
      }

      /** Sideways samples row: arrows appear only when it overflows and are disabled at either end. */
      setupSamplesRow() {
        this.row = this.querySelector('[data-scentt-samples-list]');
        this.nav = this.querySelector('[data-scentt-samples-nav]');
        if (!this.row || !this.nav) return;
        this.onRowScroll = () => this.updateRow();
        this.row.addEventListener('scroll', this.onRowScroll, { passive: true });
        if ('ResizeObserver' in window) {
          this.rowObserver = new ResizeObserver(() => this.updateRow());
          this.rowObserver.observe(this.row);
        }
        this.updateRow();
      }

      updateRow() {
        const { row, nav } = this;
        const room = row.scrollWidth - row.clientWidth;
        nav.hidden = room <= 2;
        const [prev, next] = nav.querySelectorAll('[data-scentt-samples-step]');
        if (prev) prev.disabled = row.scrollLeft <= 2;
        if (next) next.disabled = row.scrollLeft >= room - 2;
      }

      onClick(e) {
        const step = e.target.closest('[data-scentt-samples-step]');
        if (step && this.row) {
          const dir = step.getAttribute('data-scentt-samples-step') === '-1' ? -1 : 1;
          this.row.scrollBy({ left: dir * Math.max(120, this.row.clientWidth * 0.8), behavior: S.reducedMotion ? 'auto' : 'smooth' });
          return;
        }
        const btn = e.target.closest('[data-scentt-sample]');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        const key = btn.getAttribute('data-key') || '';
        const id = btn.getAttribute('data-scentt-sample');
        if (this.error) this.error.hidden = true;
        let job;
        if (key) {
          if (!KEY.test(key)) return;
          job = S.cart.change(key, 0, { open: true, source: 'scentt-samples' });
        } else {
          if (!S.isId(id)) return;
          job = S.cart.add([{ id, quantity: 1, properties: { _sample: this.dataset.sampleLabel || 'Free sample' } }], { source: 'scentt-samples' });
        }
        this.busy(btn, true);
        job.catch((err) => this.fail(err)).finally(() => { if (btn.isConnected) this.busy(btn, false); });
      }

      onChange(e) {
        // Keep the tick box and the message out of Trade's quantity handling (see the note at the top).
        e.stopPropagation();
        const ask = e.target.closest('[data-scentt-gift-toggle]');
        if (ask) this.toggleGift(ask);
      }

      /**
       * "Make it a gift?" - one tick box: saves the gift details on the cart, adds / removes the gift product (one per
       * order) and reveals / hides the message box. Any failure puts everything back and says so.
       */
      async toggleGift(ask) {
        const on = ask.checked;
        const id = ask.getAttribute('data-scentt-gift-variant') || '';
        const key = ask.getAttribute('data-key') || '';
        const panel = this.querySelector('[data-scentt-gift-box]');
        const area = this.querySelector('[data-scentt-giftmsg]');
        const show = (v) => {
          ask.checked = v;
          if (ask.hasAttribute('aria-expanded')) ask.setAttribute('aria-expanded', v ? 'true' : 'false');
          if (panel) panel.hidden = !v;
        };
        show(on);
        if (this.pending) { clearTimeout(this.pending); this.pending = null; }
        if (this.error) this.error.hidden = true;
        ask.disabled = true;
        let saved = false;
        let cartStep = false; /* Scentt.cart already shows its own error toast */
        try {
          if (on && id && !S.isId(id)) throw new Error('bad gift variant');
          if (!on && key && !KEY.test(key)) throw new Error('bad gift line');
          const message = area ? area.value : '';
          await queued(() => this.saveAttributes(on, message));
          saved = true;
          cartStep = true;
          if (on && id && !key) {
            once.focusGift = true;
            await S.cart.add([{ id, quantity: 1 }], { source: 'scentt-gift' });
          } else if (!on && key) {
            // removes the gift line even if the gift product has since sold out
            await S.cart.change(key, 0, { open: true, source: 'scentt-gift' });
          } else if (on && area) {
            area.focus({ preventScroll: false });
          }
        } catch (err) {
          once.focusGift = false;
          show(!on);
          if (saved) queued(() => this.saveAttributes(!on, area ? area.value : '')).catch(() => {});
          this.fail(err);
          if (!cartStep) S.toast((err && err.userMessage) || S.config.strings.error, 'error');
        } finally {
          if (ask.isConnected) ask.disabled = false;
        }
      }

      onInput(e) {
        const area = e.target.closest('[data-scentt-giftmsg]');
        if (!area) return;
        const count = this.querySelector('[data-scentt-gift-count]');
        if (count) count.textContent = `${area.value.length}/${area.maxLength > 0 ? area.maxLength : 250}`;
        this.area = area;
        clearTimeout(this.pending);
        this.pending = setTimeout(() => this.saveMessage(), 600);
      }

      /**
       * Saved straight to /cart/update.js (not Scentt.cart.attributes) so the cart page does not
       * reload while the shopper is typing. The value is plain text; it is escaped when rendered.
       */
      saveMessage() {
        this.pending = null;
        const area = this.area;
        if (!area) return Promise.resolve();
        const value = String(area.value).slice(0, 250);
        const attributes = { 'Gift message': value };
        // No tick box (message box always shown) and no gift line in the bag: the message alone marks the order as
        // a gift. With a gift line in the bag the order stays a gift whatever the message says.
        if (!this.querySelector('[data-scentt-gift-toggle]') && !this.querySelector('[data-scentt-gift-line]')) {
          attributes.Gift = value.trim() ? 'Yes' : '';
        }
        return queued(() => S.fetchJSON('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        })).then(() => {
          const saved = this.querySelector('[data-scentt-gift-saved]');
          if (!saved) return;
          saved.hidden = false;
          setTimeout(() => { saved.hidden = true; }, 1600);
        }).catch((err) => {
          this.fail(err);
          S.toast((err && err.userMessage) || S.config.strings.error, 'error');
        });
      }
    }
    customElements.define('scentt-cart-extras', ScenttCartExtras);
  };

  if (window.Scentt) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
